/**
 * The always-true labels (legal-pages ticket 06): what a score is, that a
 * rewrite was written with AI, and where a job page's text came from.
 *
 * The wording is counsel's draft, `.scratch/legal-pages/drafts/in-product-notices.md`
 * A-7 to A-9. Change it there first, then here: every screen reads these, so
 * no two can drift.
 *
 * They do not wait for `legal.published`: each is true today.
 */

/** A-7. Where a screen first introduces a score and has room for a sentence. */
export const SCORE_ESTIMATE_LINE =
  "This score is ClimbJobs's own estimate, based on our rules. It is guidance, not a prediction of what an employer or their software will decide.";

/** A-7's short form, for a tight space. One per screen, like the line. */
export const SCORE_ESTIMATE_SHORT = "Our estimate · not an employer's score";

/** A-8. Above a résumé a Tailor or Rebuild run wrote, and above rewrite suggestions. */
export const AI_OUTPUT_LINE =
  "Written with AI. Check every change — keep only what's true for you. You're responsible for what you send to employers.";

const possessive = (name: string) => name + (/s$/i.test(name) ? "'" : "'s");

/** A-9. Under a job's summary; the link after it is the posting itself. */
export const jobSourceLead = (company: string): string =>
  'Summarised by ClimbJobs from ' + (company.trim() ? possessive(company.trim()) : "the employer's") + ' careers page.';
export const JOB_SOURCE_LINK = 'View the original posting';

/** A-9. In the job page's secondary area. */
export const jobNotAffiliated = (company: string): string =>
  'ClimbJobs is not affiliated with ' + (company.trim() || 'this employer') + '.';

/* ── legal-pages tickets 04, 03 and 07: the upload notice, the guest delete,
      the agreement boxes and the checkout's renewal terms. Counsel's draft is
      A-1 to A-4 of the same file. ── */

/**
 * The documents an agreement sentence names, in the order it names them, with
 * the short name (the upload notice) and the full one (the sign-in box). A
 * sentence names only the ones that have a page (`legal.documents`), so
 * "and Privacy Policy" appears by itself the day that page exists.
 */
export const AGREEMENT_DOCUMENTS = [
  { slug: 'terms', short: 'Terms', full: 'Terms of Service' },
  { slug: 'privacy', short: 'Privacy Policy', full: 'Privacy Policy' },
] as const;

/** A-1. Under both guest upload controls. True today, so it does not wait for `legal.published`. */
export const UPLOAD_NOTICE =
  "Your résumé can be processed by AI providers or custom models.";
/** A-1's lead, before the document names. Only once the legal pages are published. */
export const UPLOAD_AGREE_LEAD = 'By uploading, you agree to our';

/** A-2. The guest report's delete control, its confirmation and every answer the delete can get. */
export const DELETE_UPLOAD = {
  button: 'Delete my upload',
  title: 'Delete this résumé and its report now?',
  /** On the failed-analysis screen: there is no report. */
  titleNoReport: 'Delete this résumé now?',
  body: "This can't be undone.",
  confirm: 'Delete',
  confirming: 'Deleting…',
  cancel: 'Cancel',
  close: 'Close',
  /** On /start afterwards. */
  done: 'Deleted. Your résumé and its report are gone.',
  doneNoReport: 'Deleted. Your résumé is gone.',
  /** 409 `busy`: a worker is inside a model call for this upload; the same request succeeds a few seconds later. */
  busy: 'Still working on your résumé — try again in a moment.',
  /** 409 `account_upload`: nothing was deleted, so it never says so. */
  inAccount: "This résumé is saved in your account, so it can't be deleted here. Sign in and delete it from your résumé library.",
  /**
   * 401 (no guest session in this browser) and a 404 on a first attempt (the
   * server answers 404 to another guest's cookie too, and the résumé may by now
   * be in an account). Neither proves anything was removed, so it claims nothing.
   */
  notFound: "We couldn't find this upload from this browser, so there is nothing to remove here. It may already be gone.",
} as const;

/** A-3. The sign-in box and the in-app agreement step; the document names follow. */
export const AGREE_LEAD = 'I am 18 or older and I agree to the';
/** Beside any agreement box left unticked when its button is pressed. */
export const TICK_TO_CONTINUE = 'Tick the box to continue';

/** The in-app agreement step, for an account whose recorded version is not the current one. */
export const TERMS_STEP = {
  /** No version on record: the account predates the published terms. */
  published: "We've published our Terms of Service",
  /** An older version on record. */
  updated: "We've updated our Terms of Service",
  body: 'Please read them and agree to keep using ClimbJobs.',
  read: 'Read the Terms of Service',
  confirm: 'Continue',
  confirming: 'Saving…',
  logOut: 'Log out',
} as const;

/** A-4. What a Pro term renews at, as the sentence says it: "each month", "every 3 months". */
export const RENEWAL_PERIOD = { monthly: 'month', quarterly: '3 months', annual: 'year' } as const;
type RenewalTerm = keyof typeof RENEWAL_PERIOD;
/** Wherever a Pro term is offered. True today, so it does not wait for `legal.published`. */
export const renewalLine = (term: RenewalTerm): string => 'Renews automatically each ' + RENEWAL_PERIOD[term] + ' · cancel any time';
/** The same, inside a longer line that already started. */
export const renewalClause = (term: RenewalTerm): string => 'renews automatically each ' + RENEWAL_PERIOD[term] + ' · cancel any time';

/** A-4. The confirmation step before the redirect to payment. No refund sentence: no refund path exists yet (ticket 13). */
export const CHECKOUT_STEP = {
  title: 'Your plan renews automatically.',
  body: (price: string, term: RenewalTerm): string =>
    "You'll be charged " + price + ' today and every ' + RENEWAL_PERIOD[term] + ' until you cancel. Cancel any time in Plans & Billing; you keep your plan until the period ends. Taxes may apply.',
  agreeLead: 'I agree to the automatic renewal described above and to the',
  /** The billing terms' name in that sentence: section 10 of the Terms of Service, at `legal.sections`' `billing`. */
  billingTerms: 'Subscription, Credits and Refund Terms',
  confirm: 'Continue to payment',
  confirming: 'Opening checkout…',
  cancel: 'Cancel',
} as const;
/** A-4. Beside a credit pack's button, once published. No box: nothing renews. */
export const packPurchaseLine = (credits: number, price: string): string =>
  'One-time purchase of ' + credits + ' credits for ' + price + '. Credits last 12 months.';
/** The pack card's line until then, as it has always read. */
export const PACK_VALIDITY_NOTE = 'Credits last 12 months';

/** Back from Stripe without paying (`?checkout=cancelled`, the backend's `STRIPE_CHECKOUT_CANCEL_PATH`). */
export const CHECKOUT_CANCELLED = 'Checkout was cancelled. Nothing was charged.';
